import {
  CircleHelp,
  Facebook,
  FileText,
  Info,
  Instagram,
  Lock,
  Mail,
  Phone,
  Twitter,
  Youtube,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { KRUMAGO } from "../../s3-assets/Krumago";
import FormatByOperatingLocation from "../../../components/FormatByOperatingLocation";
const EducationDomainFooter = () => {
  const company = useSelector((state) => state.companyReducer.company) || {};
  const number = company?.contactPerson?.phoneNumber || "244-5333-7783";
  const code = company?.contactPerson?.countryCode || "+1";
  const phoneNumber = `${code} ${number}`;
  const email = company?.contactPerson?.email || "avpstech.com";

  const navigate = useNavigate();

  // Get the company data from Redux store
  const companyDetails = useSelector((state) => state?.companyReducer?.company);

  // Get legal documents from company
  const legalDocuments = companyDetails?.legalDocuments;

  // Format contentSubType for display
  const formatContentSubType = (contentSubType) => {
    if (!contentSubType) return "Legal Document";
    return contentSubType
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle policy link click
  const handlePolicyClick = (doc, event) => {
    event.preventDefault();

    if (doc.contentSubType) {
      // Navigate to policy page with contentSubType as parameter
      const policyName = doc.contentSubType.toLowerCase();
      navigate(`/policy/${policyName}`);
    }
  };

  // Transform legal documents for legal links
  const legalLinks = legalDocuments?.length > 0
    ? legalDocuments.map(doc => ({
      text: formatContentSubType(doc.contentSubType) || doc.name || "Legal Document",
      document: doc,
      link: "#",
      // Choose appropriate icon based on content type
      icon: doc.contentSubType?.toLowerCase().includes('privacy') ? Lock : FileText
    }))
    : null;

  return (
    <footer className="bg-gray-100 py-6" id="footer">
      <div className="container mx-auto px-4" id="footer-container">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" id="footer-grid">
          {/* Social Media Links */}
          <div id="footer-social-media">
            <h6 className="font-bold text-lg mb-2 text-gray-800" id="social-heading">Follow Us</h6>
            <div className="flex space-x-2" id="social-icons">
              {companyDetails?.socialMediaAccounts?.map((account, index) => {
                const lowerCaseName = account.name.toLowerCase();
                let IconComponent;

                switch (lowerCaseName) {
                  case "twitter":
                    IconComponent = Twitter;
                    break;
                  case "youtube":
                    IconComponent = Youtube;
                    break;
                  case "facebook":
                    IconComponent = Facebook;
                    break;
                  case "instagram":
                    IconComponent = Instagram;
                    break;
                  default:
                    return null;
                }

                if (!account.value) return null;

                return (
                  <a
                    key={index}
                    id={`social-icon-${lowerCaseName}`}
                    href={account.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`hover:bg-gray-100 rounded-full p-2 transition ${lowerCaseName === "twitter"
                      ? "text-blue-700"
                      : lowerCaseName === "youtube"
                        ? "text-red-600"
                        : lowerCaseName === "facebook"
                          ? "text-blue-800"
                          : "text-pink-500"
                      }`}
                  >
                    <IconComponent size={28} strokeWidth={1.8} aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div id="footer-quick-links">
            <h6 className="font-bold text-lg mb-2 text-gray-800" id="quick-links-heading">Quick Links</h6>
            <div className="flex flex-col space-y-2" id="quick-links-list">
              <Link to="/contact" id="link-help" className="text-gray-700 flex items-center hover:underline">
                <CircleHelp className="mr-2" size={20} aria-hidden="true" />
                Help
              </Link>
              <Link to="/about" id="link-about-us" className="text-gray-700 flex items-center hover:underline">
                <Info className="mr-2" size={20} aria-hidden="true" />
                About Us
              </Link>
            
            </div>
          </div>

          {/* Contact Information */}
           <div id="footer-contact-info">
            <h6 className="font-bold text-lg mb-2 text-gray-800" id="contact-heading">Contact</h6>
            <div className="flex flex-col space-y-2" id="contact-details">
              <span className="text-gray-700 flex items-center" id="contact-phone">
                <Phone className="mr-2" size={20} aria-hidden="true" />
           <FormatByOperatingLocation
  value={phoneNumber}
  type="PHONE"
/>

              </span>
              <a
                href={`mailto:${email}`}
                id="contact-email"
                className="text-gray-700 flex items-center hover:underline"
              >
                <Mail className="mr-2" size={20} aria-hidden="true" />
                {email}
              </a>
            </div>
          </div>

          {/* Legal Links */}
          <div id="footer-legal">
            <h6 className="font-bold text-lg mb-2 text-gray-800" id="legal-heading">Legal</h6>
            {legalLinks ? (
              <div className="flex flex-col space-y-2" id="legal-links-list">
                {legalLinks.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <a
                      key={index}
                      href={item.link}
                      id={`link-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-700 flex items-center hover:underline"
                      onClick={(e) => item.document ? handlePolicyClick(item.document, e) : null}
                    >
                      <IconComponent className="mr-2" size={20} aria-hidden="true" />
                      {item.text}
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-600 text-sm italic" id="legal-message">
                Contact admin to add legal documents
              </p>
            )}
          </div>
        </div>

        <hr className="my-4" id="footer-divider" />

        <p className="text-center text-gray-600" id="footer-copyright">
          © {new Date().getFullYear()} {companyDetails?.name || "AVPS Academy"}. All rights reserved.
        </p>
        <p className="text-center text-gray-600 mt-1" id="footer-powered-by">
          <strong>Powered by</strong>{" "}
          <img
            loading="lazy"
            src={KRUMAGO.IMAGES.KRUMAGO_IMG}
            alt="Krumago"
            className="max-h-6 inline-block align-middle ml-1 cursor-pointer"
            id="krumago-logo"
          />
          <br />
          <a
            href="https://www.krumago.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
            id="krumago-link"
          >
            www.krumago.com
          </a>
        </p>
      </div>
    </footer>
  );

};

export default EducationDomainFooter;
